import { generateId, type FileUIPart } from "ai";
import {
  useImagineChat,
  SendMessageResult,
} from "@/providers/imagine-chat-provider";
import { PromptAddonType } from "@/stores/slices/prompt-context.slice";
import { imagineStore } from "@/stores/imagine.store";
import { useCallback } from "react";

export function useSendMessage(): (params: {
  projectId: string;
  text: string;
  files?: Array<FileUIPart>;
}) => Promise<SendMessageResult | undefined> {
  const allAddons = imagineStore.use.promptContext.addons();
  const clearAddonsByType = imagineStore.use.promptContext.clearAddonsByType();
  const sandboxClient = imagineStore.use.sandboxState.sandboxClient();
  const { _sendMessage } = useImagineChat();

  const handleSendMessage = useCallback(
    async ({
      projectId,
      text,
      files,
    }: {
      projectId: string;
      text: string;
      files?: Array<FileUIPart>;
    }) => {
      // TODO: make sure when resubmitting on error, it clears out the latest user messages
      // if (messages[messages.length - 1]?.role === "user") {
      //   setMessages(messages.slice(0, -1));
      // }

      const attachmentAddons = allAddons
        .filter((addon) => addon.type === PromptAddonType.ATTACHMENT)
        .map((attachment) => ({
          type: "file",
          filename: attachment.props.filename,
          url: attachment.props.url,
          mediaType: attachment.props.mediaType,
        })) satisfies Array<FileUIPart>;

      const allFiles = [...(files ?? []), ...attachmentAddons];

      const previewRouteAddon = allAddons.find(
        (addon) => addon.type === PromptAddonType.PREVIEW_ROUTE,
      );

      const selectedElementAddons = allAddons.filter(
        (addon) => addon.type === PromptAddonType.SELECTED_ELEMENT,
      );

      let metadata: Record<string, unknown> | undefined;

      if (previewRouteAddon) {
        metadata = {
          ...metadata,
          previewActiveRoute: previewRouteAddon.props.path,
        };
      }

      if (selectedElementAddons.length > 0) {
        metadata = {
          ...metadata,
          selectedElements: selectedElementAddons.map((addon) => ({
            xpath: addon.props.xpath,
            uniqueId: addon.props.uniqueId,
            pathname: addon.props.pathname,
            previewText: addon.props.previewText,
            tagName: addon.props.tagName,
            elementId: addon.props.elementId,
            className: addon.props.className,
          })),
        };
      }

      // Send the message
      const response = await _sendMessage({
        projectId: projectId!,
        message: {
          id: `usr-${generateId()}`,
          role: "user",
          parts: [
            {
              type: "text",
              text,
            },
            ...allFiles,
          ],
          ...(metadata ? { metadata } : {}),
        },
      });

      // Post-send cleanup: handle sandbox side effects and clear temporary addons
      if (selectedElementAddons.length > 0 && sandboxClient) {
        selectedElementAddons.forEach((addon) => {
          sandboxClient.deselectElement(addon.props.uniqueId);
        });
      }

      clearAddonsByType(PromptAddonType.ATTACHMENT);
      clearAddonsByType(PromptAddonType.SELECTED_ELEMENT);

      return response;
    },
    [_sendMessage, allAddons, sandboxClient, clearAddonsByType],
  );

  return handleSendMessage;
}
